import { Routes } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { analyticsConsentGuard } from './core/guards/analytics-consent/analytics-consent.guard';
import { acknowledgementsGuard } from './core/guards/acknowledgements/acknowledgements.guard';
import { openBankingReturnGuard } from './core/guards/open-banking-return/open-banking-return.guard';
import { clientSetupGuard } from './core/guards/client-setup/client-setup.guard';
import { phoneVerifiedGuard } from './core/guards/phone-verification/phone-verified.guard';
import { dashboardResolver } from './dashboard/dashboard.resolver';
import { marketingPreferencesResolver } from './features/marketing-preferences/marketing-preferences.resolver';
import { notificationsResolver } from './features/notifications/notifications.resolver';
import { openBankingResolver } from './features/open-banking/open-banking.resolver';
import { applicationsRoutes } from './applications/applications.routes';
import { authRoutes } from './routes/auth.routes';
import { documentsRoutes } from './routes/documents.routes';
import { factFindRoutes } from './routes/fact-find.routes';
import { phoneVerificationRoutes } from './routes/phone-verification.routes';
import { preQualifyRoutes } from './features/pre-qualify/pre-qualify.routes';
import { brandingResolver } from './core/resolvers/branding.resolver';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    loadComponent: () => import('./home/home.component').then((m) => m.HomeComponent),
  },
  {
    path: 'invite/:brokerName',
    loadComponent: () => import('./home/home.component').then((m) => m.HomeComponent),
  },
  {
    path: 'auth',
    children: authRoutes,
  },
  {
    path: 'yoti-iframe',
    loadComponent: () =>
      import('./components/identity-verification-content/yoti-iframe-page.component').then(
        (m) => m.YotiIframePageComponent
      ),
  },
  {
    path: 'form-fields',
    loadComponent: () =>
      import('./features/form-fields/form-fields.component').then((m) => m.FormFieldsComponent),
  },
  {
    path: 'error',
    loadComponent: () => import('./error.component').then((m) => m.ErrorComponent),
  },
  {
    path: 'pre-qualify',
    children: preQualifyRoutes,
  },
  {
    path: '',
    canActivate: [MsalGuard],
    children: [
      {
        path: '',
        canActivate: [clientSetupGuard],
        children: [
          {
            path: 'analytics-consent',
            resolve: { branding: brandingResolver },
            loadComponent: () =>
              import('./features/analytics-consent/analytics-consent.component').then(
                (m) => m.AnalyticsConsentComponent
              ),
          },
          {
            path: 'acknowledgements',
            loadComponent: () =>
              import('./features/acknowledgements/acknowledgements.component').then(
                (m) => m.AcknowledgementsComponent
              ),
          },
          {
            path: 'phone-verification',
            children: phoneVerificationRoutes,
          },
          {
            path: '',
            canActivate: [
              analyticsConsentGuard,
              acknowledgementsGuard,
              phoneVerifiedGuard,
            ],
            resolve: { branding: brandingResolver },
            children: [
              {
                path: 'identity-verification-complete',
                loadComponent: () =>
                  import(
                    './features/identity-verification/identity-verification-complete.component'
                  ).then((m) => m.IdentityVerificationCompleteComponent),
              },
              {
                path: 'identity-verification-failed',
                loadComponent: () =>
                  import(
                    './features/identity-verification/identity-verification-failed.component'
                  ).then((m) => m.IdentityVerificationFailedComponent),
              },
              {
                path: 'manual-identity-verification',
                loadComponent: () =>
                  import(
                    './components/identity-verification-content/manual-idv-verification-content.component'
                  ).then((m) => m.ManualIdvVerificationContentComponent),
              },
              {
                path: 'introduction',
                loadComponent: () =>
                  import('./features/introduction/introduction.component').then(
                    (m) => m.IntroductionComponent
                  ),
              },
              {
                path: 'dashboard',
                loadComponent: () =>
                  import('./dashboard/dashboard.component').then((m) => m.DashboardComponent),
                resolve: { ready: dashboardResolver },
              },
              {
                path: 'notifications',
                loadComponent: () =>
                  import('./features/notifications/notifications.component').then(
                    (m) => m.NotificationsComponent
                  ),
                resolve: { notifications: notificationsResolver },
              },
              {
                path: 'fact-find',
                children: factFindRoutes,
              },
              {
                path: 'marketing-preferences',
                loadComponent: () =>
                  import('./features/marketing-preferences/marketing-preferences.component').then(
                    (m) => m.MarketingPreferencesComponent
                  ),
                resolve: {
                  marketingPreferences: marketingPreferencesResolver,
                },
              },
              {
                path: 'open-banking',
                loadComponent: () =>
                  import('./features/open-banking/open-banking.component').then(
                    (m) => m.OpenBankingComponent
                  ),
                resolve: {
                  data: openBankingResolver,
                },
                children: [
                  { path: '', redirectTo: 'dob', pathMatch: 'full' },
                  {
                    path: 'dob',
                    loadComponent: () =>
                      import(
                        './features/open-banking/components/question-step/question-step.component'
                      ).then((m) => m.ObQuestionStepComponent),
                  },
                  {
                    path: 'current-address',
                    loadComponent: () =>
                      import(
                        './features/open-banking/components/question-step/question-step.component'
                      ).then((m) => m.ObQuestionStepComponent),
                  },
                  {
                    path: 'current-address/move-in',
                    loadComponent: () =>
                      import(
                        './features/open-banking/components/question-step/question-step.component'
                      ).then((m) => m.ObQuestionStepComponent),
                  },
                  {
                    path: 'previous-address/:index',
                    loadComponent: () =>
                      import(
                        './features/open-banking/components/question-step/question-step.component'
                      ).then((m) => m.ObQuestionStepComponent),
                  },
                  {
                    path: 'previous-address/:index/move-in',
                    loadComponent: () =>
                      import(
                        './features/open-banking/components/question-step/question-step.component'
                      ).then((m) => m.ObQuestionStepComponent),
                  },
                  {
                    path: 'previous-address/:index/move-out',
                    loadComponent: () =>
                      import(
                        './features/open-banking/components/question-step/question-step.component'
                      ).then((m) => m.ObQuestionStepComponent),
                  },
                  {
                    path: 'confirmation',
                    loadComponent: () =>
                      import(
                        './features/open-banking/components/confirmation/confirmation.component'
                      ).then((m) => m.ObConfirmationComponent),
                  },
                  {
                    path: 'in-progress',
                    loadComponent: () =>
                      import(
                        './features/open-banking/components/in-progress/in-progress.component'
                      ).then((m) => m.ObInProgressComponent),
                  },
                  {
                    path: 'success',
                    canActivate: [openBankingReturnGuard],
                    loadComponent: () =>
                      import(
                        './features/open-banking/components/success/success.component'
                      ).then((m) => m.ObSuccessComponent),
                  },
                  {
                    path: 'error',
                    canActivate: [openBankingReturnGuard],
                    loadComponent: () =>
                      import(
                        './features/open-banking/components/error/error.component'
                      ).then((m) => m.ObErrorComponent),
                  },
                ],
              },
              {
                path: 'applications',
                children: applicationsRoutes,
              },
              {
                path: 'documents',
                children: documentsRoutes,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '**',
    loadComponent: () => import('./page-not-found.component').then((m) => m.PageNotFoundComponent),
  },
];
